import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity, Users, ShieldCheck, Building2, Home, Sparkles, MessageSquare, LifeBuoy, BookOpen, ClipboardList, Eye, Briefcase, ArrowLeft, FileText, Bell, Trash2,
} from "lucide-react";

export const ADMIN_SECTIONS = [
  { tab: "overview", label: "Overview", icon: Activity },
  { tab: "users", label: "Users", icon: Users },
  { tab: "verification", label: "Verification", icon: ShieldCheck },
  { tab: "developments", label: "Developments", icon: Building2 },
  { tab: "units", label: "Units", icon: Home },
  { tab: "interests", label: "Interests", icon: Sparkles },
  { tab: "conversations", label: "Conversations", icon: MessageSquare },
  { tab: "updates", label: "Updates", icon: FileText },
  { tab: "notifications", label: "Notifications", icon: Bell },
  { tab: "support", label: "Support", icon: LifeBuoy },
  { tab: "guide", label: "Investor Guide", icon: BookOpen },
  { tab: "trash", label: "Recently deleted", icon: Trash2 },
  { tab: "audit", label: "Audit Log", icon: ClipboardList },
];

const VIEW_AS = [
  { to: "/discover", label: "Investor view", icon: Eye, testId: "nav-admin-investor-view", match: (p) => !p.startsWith("/admin") },
  { to: "/admin/developer-view", label: "Developer view", icon: Briefcase, testId: "nav-admin-developer-view", match: (p) => p.startsWith("/admin/developer-view") },
];

function useActiveTab() {
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get("tab") || "overview";
  return { pathname, tab: pathname === "/admin" || pathname === "/admin/support" ? tab : null };
}

const itemCls = (active) => `flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${active ? "bg-propera-light text-propera" : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"}`;

export function AdminSidebarNav() {
  const { pathname, tab } = useActiveTab();
  return (
    <nav className="px-3 flex-1 flex flex-col gap-0.5 overflow-y-auto" data-testid="admin-sidebar-nav">
      <p className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-semibold">Admin Console</p>
      {ADMIN_SECTIONS.map(({ tab: t, label, icon: Icon }) => (
        <Link key={t} to={`/admin?tab=${t}`} data-testid={`nav-admin-${t}`} className={itemCls(tab === t)}>
          <Icon size={17} strokeWidth={2} /> <span>{label}</span>
        </Link>
      ))}
      <p className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-semibold">View as</p>
      {VIEW_AS.map(({ to, label, icon: Icon, testId, match }) => (
        <Link key={to} to={to} data-testid={testId} className={itemCls(match(pathname))}>
          <Icon size={17} strokeWidth={2} /> <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
}

const MOBILE = [
  { to: "/admin?tab=overview", tab: "overview", label: "Overview", icon: Activity },
  { to: "/admin?tab=users", tab: "users", label: "Users", icon: Users },
  { to: "/admin?tab=verification", tab: "verification", label: "Verify", icon: ShieldCheck },
  { to: "/admin?tab=support", tab: "support", label: "Support", icon: LifeBuoy },
  { to: "/discover", tab: null, label: "Investor", icon: Eye },
];

export function AdminMobileNav() {
  const { pathname, tab } = useActiveTab();
  return (
    <div className="flex items-center justify-around px-2 py-2">
      {MOBILE.map(({ to, tab: t, label, icon: Icon }) => {
        const active = t ? tab === t : !pathname.startsWith("/admin");
        return (
          <Link key={to} to={to} data-testid={`mobile-nav-admin-${label.toLowerCase()}`} className={`flex flex-col items-center gap-1 px-1.5 py-1.5 rounded-xl text-[10px] ${active ? "text-propera" : "text-neutral-500"}`}>
            <Icon size={20} /> <span>{label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function InvestorViewBanner() {
  return (
    <div className="bg-neutral-900 text-white px-6 lg:px-10 py-2 flex items-center justify-between gap-3 text-xs" data-testid="investor-view-banner">
      <span className="flex items-center gap-2"><Eye size={13} className="text-emerald-400" /> Investor view: you&apos;re seeing Propera the way investors do.</span>
      <Link to="/admin?tab=overview" data-testid="investor-view-back" className="inline-flex items-center gap-1 font-semibold text-emerald-300 hover:text-white transition-colors"><ArrowLeft size={12} /> Back to Admin Console</Link>
    </div>
  );
}
